import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import {
  CREATE_VENUE,
  GET_FEATURES_BY_VENUE_ID,
  GET_PICTURES_BY_VENUE,
  GET_VENUES,
  GET_VENUE_BY_ID,
  GET_VENUE_FEATURES,
  UPDATE_VENUE,
  DELETE_VENUE_PICTURE,
} from './constants';
import {
  MESSAGE_VENUE_CREATE_FAILED,
  MESSAGE_VENUE_CREATE_SUCCESS,
} from '../../constants/form-messages';
import Api from '../../commons/api';
import * as VenueActions from './action';
import DefaultIcon from '../../assets/svg/question_mark.svg';
import { VENUE_FEATURES } from '../../constants/venue';
import { APP_ROUTES } from '../../constants/app-routes';
import { onDisplayAlert } from '../../utils/alerts/alerts';

const controller = 'venues/venues';

const baseQueryBuilder = () => RequestQueryBuilder.create();

function* fetchGetVenues() {
  try {
    const { data } = yield Api.get(controller);
    yield put(VenueActions.getVenuesSuccess(data));
  } catch (err) {
    yield put(VenueActions.getVenuesFailed(err));
  }
}

function* fetchGetVenueFeatures() {
  try {
    const { data } = yield Api.get(`${controller}/venue-features`);
    const features = data.map((item) => {
      const VF = VENUE_FEATURES.find((fe) => fe.id === item.id);
      return {
        ...item,
        state: null,
        Icon: VF ? VF.Icon : DefaultIcon,
      };
    });
    yield put(VenueActions.getVenueFeaturesSuccess(features));
  } catch (err) {
    yield put(VenueActions.getVenueFeaturesFailed(err));
  }
}

const generatePictures = (pictures) =>
  pictures.map((i) => ({
    type: 'venue',
    key: i.key,
    name: i.fileName,
  }));

function* fetchCreateVenue({ payload }) {
  const { venue, pictures, features, socialNetworks } = payload.venue;
  const venueStorage = { ...venue };
  if (pictures.length > 0) venue.banner = pictures[0].key;
  try {
    const {
      data: { id },
    } = yield Api.post(`${controller}`, { venue, features, socialNetworks });
    venueStorage.id = id;
    if (pictures?.length > 0) {
      const multimediaList = generatePictures(pictures);
      yield Api.post(`${controller}/multimedia?venue_id=${id}`, multimediaList);
    }
    localStorage.setItem('currentVenue', JSON.stringify(venueStorage));
    yield put(VenueActions.createVenueSuccess(venueStorage, MESSAGE_VENUE_CREATE_SUCCESS));
    yield put(VenueActions.getPicturesByVenue(id));

    payload.navigate(APP_ROUTES.DASHBOARD);

    onDisplayAlert({
      type: 'success',
      title: 'Venue Information',
      description:
        "You've successfully entered your venue details.<br>You can now start creating events.",
    });
  } catch (err) {
    yield put(VenueActions.createVenueFailed(MESSAGE_VENUE_CREATE_FAILED));
  }
}

function* fetchGetPicturesByVenue({ payload: { id } }) {
  try {
    const { data } = yield Api.get(`${controller}/${id}/pictures`);
    yield put(VenueActions.getPicturesByVenueSuccess(data));
  } catch (err) {
    yield put(VenueActions.getPicturesByVenueFailed(err));
  }
}

function* deleteVenuePictureSaga(action) {
  try {
    yield Api.delete(`${controller}/multimedia`, { key: action.payload });
    yield put(VenueActions.deleteVenuePictureSuccess(action.payload));
  } catch (err) {
    yield put(VenueActions.deleteVenuePictureFailure(err));
  }
}

function* GetFeaturesByVenueId({ payload }) {
  try {
    const query = baseQueryBuilder()
      .setJoin({ field: 'link_venue_features' })
      .setJoin({ field: 'link_venue_features.venue_feature' })
      .query();
    const { data } = yield Api.get(`${controller}/${payload.venueId}?${query}`);
    yield put(VenueActions.getFeaturesByVenueIdSuccess(data));
  } catch (err) {
    yield put(VenueActions.getFeaturesByVenueIdFailed(err));
  }
}

function* fetchGetVenueById({ payload }) {
  try {
    const query = baseQueryBuilder()
      .setJoin({ field: 'city' })
      .setJoin({ field: 'state' })
      .setJoin({ field: 'link_venue_features' })
      .setJoin({ field: 'link_venue_features.venue_feature' })
      .setJoin({ field: 'social_network' })
      .query();
    const { data } = yield Api.get(`${controller}/${payload.id}?${query}`);
    yield put(VenueActions.getPicturesByVenue(payload.id));
    yield put(VenueActions.getVenueByIdSuccess(data));
  } catch (err) {
    yield put(VenueActions.getVenueByIdFailed(err));
  }
}

function* fetchUploadEvent({ payload }) {
  try {
    const { venue } = payload;
    // Ensure venue.id is a number
    const venueId = Number(venue.id);
    // if (isNaN(venueId)) {
    //  throw new Error('Invalid venue ID: must be a number');
    // }

    // Handle new pictures upload if any
    const { pictures } = venue;
    if (pictures?.length > 0) {
      const multimediaList = generatePictures(pictures);
      yield Api.post(`${controller}/multimedia?venue_id=${venueId}`, multimediaList);
      // If banner is not set and we have new pictures, set the first one as banner
      if (!venue.banner && pictures[0]?.key) {
        venue.banner = pictures[0].key;
      }
    }

    // Remove pictures from the venue object before sending to update endpoint
    // (pictures are handled separately via multimedia endpoint)
    const { pictures: _, ...venueUpdateData } = venue;

    const { data } = yield Api.put(`${controller}/${venueId}`, venueUpdateData);
    yield put(VenueActions.updateVenueSuccess(data));
    yield put(VenueActions.getVenues());
    yield put(VenueActions.getPicturesByVenue(venueId));
    onDisplayAlert({ title: 'Saved!', description: 'Venue updated', type: 'success' });
  } catch (err) {
    yield put(VenueActions.updateVenueFailed(err));
  }
}

function* ActionWatcher() {
  yield takeLatest(CREATE_VENUE, fetchCreateVenue);
  yield takeLatest(GET_VENUES, fetchGetVenues);
  yield takeLatest(GET_VENUE_FEATURES, fetchGetVenueFeatures);
  yield takeLatest(GET_FEATURES_BY_VENUE_ID, GetFeaturesByVenueId);
  yield takeLatest(UPDATE_VENUE, fetchUploadEvent);
  yield takeLatest(DELETE_VENUE_PICTURE, deleteVenuePictureSaga);
  yield takeLatest(GET_PICTURES_BY_VENUE, fetchGetPicturesByVenue);
  yield takeLatest(GET_VENUE_BY_ID, fetchGetVenueById);
}

export default function* VenueSaga() {
  yield all([ActionWatcher()]);
}
